import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MainLayout } from '@/layouts/MainLayout';
import { Container, Stack, Button, Alert, Skeleton, PageHeader } from '@/components/ui';
import { CompanyTable, CompanyFilters, CompanyForm } from '@/components/companyManagement';
import { ConfirmationDialog } from '@/components/userManagement';
import { CompanyManagementService } from '@/services/companyManagement.service';
import { PoolPageTypeNameModal } from '@/components/poolPageTypeName';
import { PoolManagementModal } from '@/components/poolManagement';
import { useToast } from '@/contexts/ToastContext';
import type {
  Company,
  CompanyFilter,
  CompanyGroup,
  PoolOption,
  CompanySortField,
  SortDirection,
  CreateCompanyRequest,
  UpdateCompanyRequest,
} from '@/types/companyManagement.types';

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function filterCompanies(companies: Company[], filters: CompanyFilter): Company[] {
  return companies.filter((company) => {
    if (filters.search) {
      const term = filters.search.toLowerCase();
      if (!company.name.toLowerCase().includes(term)) return false;
    }

    if (filters.poolId !== undefined) {
      const hasPool = company.poolIds.some((p) => p.poolId === filters.poolId);
      if (!hasPool) return false;
    }

    if (filters.groupCode) {
      const hasGroup = company.groups.some((g) => g.code === filters.groupCode);
      if (!hasGroup) return false;
    }

    if (filters.isInactive !== undefined) {
      if (company.isInactive !== filters.isInactive) return false;
    }

    return true;
  });
}

function sortCompanies(
  companies: Company[],
  sortField: CompanySortField,
  sortDirection: SortDirection
): Company[] {
  const dir = sortDirection === 'asc' ? 1 : -1;
  const locale: Intl.LocalesArgument = undefined;
  const collate: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };

  return [...companies].sort((a, b) => {
    let cmp = 0;

    switch (sortField) {
      case 'id':
        cmp = a.id - b.id;
        break;
      case 'name':
        cmp = a.name.localeCompare(b.name, locale, collate);
        break;
      case 'startDate': {
        const aTime = a.startDate ? new Date(a.startDate).getTime() : null;
        const bTime = b.startDate ? new Date(b.startDate).getTime() : null;
        if (aTime === null && bTime === null) break;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        cmp = aTime - bTime;
        break;
      }
      case 'endDate': {
        const aTime = a.endDate ? new Date(a.endDate).getTime() : null;
        const bTime = b.endDate ? new Date(b.endDate).getTime() : null;
        if (aTime === null && bTime === null) break;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        cmp = aTime - bTime;
        break;
      }
      case 'status':
        // Active (isInactive=false=0) sorts before Inactive (isInactive=true=1)
        cmp = Number(a.isInactive) - Number(b.isInactive);
        break;
      case 'pools': {
        const aPool = a.poolIds[0]?.pool ?? null;
        const bPool = b.poolIds[0]?.pool ?? null;
        if (aPool === null && bPool === null) break;
        if (aPool === null) return 1;
        if (bPool === null) return -1;
        cmp = aPool.localeCompare(bPool, locale, collate);
        break;
      }
      case 'groups': {
        const aGroup = a.groups[0]?.name ?? null;
        const bGroup = b.groups[0]?.name ?? null;
        if (aGroup === null && bGroup === null) break;
        if (aGroup === null) return 1;
        if (bGroup === null) return -1;
        cmp = aGroup.localeCompare(bGroup, locale, collate);
        break;
      }
      case 'userCount':
        cmp = a.userCount - b.userCount;
        break;
      case 'lastLoginAt': {
        const aTime = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : null;
        const bTime = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : null;
        if (aTime === null && bTime === null) break;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        cmp = aTime - bTime;
        break;
      }
      default:
        return 0;
    }

    if (cmp !== 0) return cmp * dir;
    // Secondary: stable alphabetical by name
    return a.name.localeCompare(b.name, locale, { sensitivity: 'base' });
  });
}

// ---------------------------------------------------------------------------
// State types
// ---------------------------------------------------------------------------

interface PageState {
  allCompanies: Company[];
  pools: PoolOption[];
  groups: CompanyGroup[];
  isCompaniesLoading: boolean;
  isMetadataLoading: boolean;
  error: string | null;
  filters: CompanyFilter;
  currentPage: number;
  pageSize: number;
  sortField: CompanySortField;
  sortDirection: SortDirection;
}

interface DialogState {
  showForm: boolean;
  showDeleteConfirm: boolean;
  showPageTypeNames: boolean;
  showPoolManagement: boolean;
  editingCompany: Company | null;
  deletingCompany: Company | null;
  formLoading: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CompanyManagementPage: React.FC = () => {
  const { showToast } = useToast();
  const [state, setState] = useState<PageState>({
    allCompanies: [],
    pools: [],
    groups: [],
    isCompaniesLoading: true,
    isMetadataLoading: true,
    error: null,
    filters: {},
    currentPage: 1,
    pageSize: 25,
    sortField: 'id',
    sortDirection: 'desc',
  });

  const [dialogs, setDialogs] = useState<DialogState>({
    showForm: false,
    showDeleteConfirm: false,
    showPageTypeNames: false,
    showPoolManagement: false,
    editingCompany: null,
    deletingCompany: null,
    formLoading: false,
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Load companies
  const loadCompanies = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isCompaniesLoading: true, error: null }));
      const response = await CompanyManagementService.getCompanies();
      setState((prev) => ({
        ...prev,
        allCompanies: response.companies,
        isCompaniesLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load companies',
        isCompaniesLoading: false,
      }));
    }
  }, []);

  // Load pools and groups (metadata) once on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        setState((prev) => ({ ...prev, isMetadataLoading: true }));
        const [poolsData, groupsData] = await Promise.all([
          CompanyManagementService.getPools(),
          CompanyManagementService.getAvailableGroups(),
        ]);
        setState((prev) => ({
          ...prev,
          pools: poolsData,
          groups: groupsData,
          isMetadataLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to load metadata',
          isMetadataLoading: false,
        }));
      }
    };

    loadMetadata();
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Filtering + sorting + pagination
  const filteredCompanies = useMemo(() => {
    const filtered = filterCompanies(state.allCompanies, state.filters);
    return sortCompanies(filtered, state.sortField, state.sortDirection);
  }, [state.allCompanies, state.filters, state.sortField, state.sortDirection]);

  const paginatedCompanies = useMemo(() => {
    const start = (state.currentPage - 1) * state.pageSize;
    return filteredCompanies.slice(start, start + state.pageSize);
  }, [filteredCompanies, state.currentPage, state.pageSize]);

  const totalPages = Math.ceil(filteredCompanies.length / state.pageSize);
  const totalCount = filteredCompanies.length;

  const paginationItems = useMemo<(number | string)[]>(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const items: (number | string)[] = [1];
    const start = Math.max(2, state.currentPage - 1);
    const end = Math.min(totalPages - 1, state.currentPage + 1);
    if (start > 2) items.push('left-ellipsis');
    for (let page = start; page <= end; page++) items.push(page);
    if (end < totalPages - 1) items.push('right-ellipsis');
    items.push(totalPages);
    return items;
  }, [state.currentPage, totalPages]);

  const activeFilterCount = Object.values(state.filters).filter(
    (v) => v !== undefined && v !== ''
  ).length;

  // Handlers
  const handleFiltersChange = useCallback((newFilters: CompanyFilter) => {
    setState((prev) => ({ ...prev, filters: newFilters, currentPage: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setState((prev) => ({ ...prev, filters: {}, currentPage: 1 }));
  }, []);

  const handleSort = (field: CompanySortField) => {
    setState((prev) => ({
      ...prev,
      sortField: field,
      sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (page: number) => {
    setState((prev) => ({ ...prev, currentPage: page }));
  };

  const handleCreateCompany = () => {
    setDialogs((prev) => ({ ...prev, showForm: true, editingCompany: null }));
  };

  const handleEditCompany = (company: Company) => {
    setDialogs((prev) => ({ ...prev, showForm: true, editingCompany: company }));
  };

  const handleFormSubmit = async (data: CreateCompanyRequest | UpdateCompanyRequest) => {
    try {
      setDialogs((prev) => ({ ...prev, formLoading: true }));

      if (dialogs.editingCompany) {
        await CompanyManagementService.updateCompany(
          dialogs.editingCompany.id,
          data as UpdateCompanyRequest
        );
        showToast('Company updated successfully', { variant: 'success' });
      } else {
        await CompanyManagementService.createCompany(data as CreateCompanyRequest);
        showToast('Company created successfully', { variant: 'success' });
      }

      await loadCompanies();
      setDialogs((prev) => ({
        ...prev,
        showForm: false,
        editingCompany: null,
        formLoading: false,
      }));
    } catch (error) {
      setDialogs((prev) => ({ ...prev, formLoading: false }));
      showToast(error instanceof Error ? error.message : 'Failed to save company', {
        variant: 'error',
      });
      throw error;
    }
  };

  const handleFormCancel = () => {
    setDialogs((prev) => ({
      ...prev,
      showForm: false,
      editingCompany: null,
      formLoading: false,
    }));
  };

  const handleDeleteCompany = (company: Company) => {
    setDialogs((prev) => ({ ...prev, showDeleteConfirm: true, deletingCompany: company }));
  };

  const handleConfirmDelete = async () => {
    if (!dialogs.deletingCompany) return;

    try {
      setDialogs((prev) => ({ ...prev, formLoading: true }));
      await CompanyManagementService.deleteCompany(dialogs.deletingCompany.id);
      await loadCompanies();
      showToast('Company deleted successfully', { variant: 'success' });
      setDialogs((prev) => ({
        ...prev,
        showDeleteConfirm: false,
        deletingCompany: null,
        formLoading: false,
      }));
    } catch (error) {
      setDialogs((prev) => ({ ...prev, formLoading: false }));
      showToast(error instanceof Error ? error.message : 'Failed to delete company', {
        variant: 'error',
      });
    }
  };

  const handleCancelDialog = () => {
    setDialogs((prev) => ({
      ...prev,
      showDeleteConfirm: false,
      deletingCompany: null,
      formLoading: false,
    }));
  };

  const handleDismissError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <MainLayout>
      <Container size="full">
        <Stack spacing={6}>
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <PageHeader title="Company Management" />
              {/* Management links */}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <button
                  type="button"
                  onClick={() => setDialogs((prev) => ({ ...prev, showPageTypeNames: true }))}
                  className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400 dark:hover:text-primary-300"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Manage Page Type Names
                </button>
                <button
                  type="button"
                  onClick={() => setDialogs((prev) => ({ ...prev, showPoolManagement: true }))}
                  className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400 dark:hover:text-primary-300"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  Manage Pools
                </button>
              </div>
            </div>

            <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-4">
              {/* Create Company — + Add on mobile, full label on sm+ */}
              <button
                type="button"
                onClick={handleCreateCompany}
                className="shrink-0 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:hidden"
              >
                + Add
              </button>
              <div className="hidden sm:block">
                <Button
                  variant="primary"
                  onClick={handleCreateCompany}
                  leftIcon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  }
                >
                  Create Company
                </Button>
              </div>

              {/* Mobile-only controls: sort + filter */}
              <div className="flex flex-1 items-center justify-end gap-2 sm:hidden">
                {/* Sort selector */}
                <div className="relative flex-1">
                  <select
                    value={`${state.sortField}-${state.sortDirection}`}
                    onChange={(e) => {
                      const [field, dir] = e.target.value.split('-') as [
                        CompanySortField,
                        SortDirection,
                      ];
                      setState((prev) => ({ ...prev, sortField: field, sortDirection: dir }));
                    }}
                    className="w-full appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-7 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-300"
                    aria-label="Sort companies"
                  >
                    <option value="name-asc">Name A–Z</option>
                    <option value="name-desc">Name Z–A</option>
                    <option value="startDate-asc">Start date ↑</option>
                    <option value="startDate-desc">Start date ↓</option>
                    <option value="endDate-asc">End date ↑</option>
                    <option value="endDate-desc">End date ↓</option>
                    <option value="pools-desc">Most pools</option>
                    <option value="groups-desc">Most roles</option>
                    <option value="userCount-desc">Most users</option>
                    <option value="lastLoginAt-desc">Last login ↓</option>
                    <option value="lastLoginAt-asc">Last login ↑</option>
                    <option value="id-desc">Newest first</option>
                    <option value="id-asc">Oldest first</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                    <svg
                      className="h-3.5 w-3.5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Filter icon */}
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(true)}
                  className="relative rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-100 dark:border-dark-600 dark:text-gray-300 dark:hover:bg-dark-700"
                  aria-label="Open filters"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                    />
                  </svg>
                  {activeFilterCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Error alert */}
          {state.error && (
            <Alert variant="error" onClose={handleDismissError}>
              {state.error}
            </Alert>
          )}

          {/* Filters — desktop only (mobile uses the right-side drawer) */}
          <div className="hidden md:block">
            {state.isMetadataLoading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-dark-600 dark:bg-dark-800">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={`filter-skeleton-${i}`}>
                      <Skeleton className="mb-2 h-4 w-24" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <CompanyFilters
                filters={state.filters}
                onFiltersChange={handleFiltersChange}
                pools={state.pools}
                groups={state.groups}
                onClearFilters={handleClearFilters}
              />
            )}
          </div>

          {/* Table */}
          <CompanyTable
            companies={paginatedCompanies}
            onEdit={handleEditCompany}
            onDelete={handleDeleteCompany}
            isLoading={state.isCompaniesLoading}
            sortField={state.sortField}
            sortDirection={state.sortDirection}
            onSort={handleSort}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="order-2 text-center text-sm text-gray-700 dark:text-gray-300 sm:order-1 sm:text-left">
                Showing {(state.currentPage - 1) * state.pageSize + 1} to{' '}
                {Math.min(state.currentPage * state.pageSize, totalCount)} of {totalCount} companies
              </div>

              <div className="order-1 flex flex-wrap items-center justify-center gap-2 sm:order-2 sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(state.currentPage - 1)}
                  disabled={state.currentPage <= 1}
                >
                  Previous
                </Button>

                {paginationItems.map((item) => {
                  if (typeof item === 'string') {
                    return (
                      <span
                        key={item}
                        className="px-2 text-sm text-gray-500 dark:text-gray-400"
                        aria-hidden="true"
                      >
                        ...
                      </span>
                    );
                  }
                  return (
                    <Button
                      key={item}
                      variant={item === state.currentPage ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </Button>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(state.currentPage + 1)}
                  disabled={state.currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Stack>
      </Container>

      {/* Company Form Modal */}
      {dialogs.showForm && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="company-form-title"
        >
          <div
            className="fixed inset-0 bg-gray-900/50 transition-opacity dark:bg-black/60"
            aria-hidden="true"
          />
          <div className="relative z-10 flex max-h-[min(92dvh,880px)] min-h-0 w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-dark-700 dark:bg-dark-800 sm:rounded-xl">
            <CompanyForm
              company={dialogs.editingCompany}
              pools={state.pools}
              groups={state.groups}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={dialogs.showDeleteConfirm}
        title="Delete Company"
        message={`Are you sure you want to delete "${dialogs.deletingCompany?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDialog}
        variant="danger"
        isLoading={dialogs.formLoading}
      />

      {/* Page Type Names Modal */}
      <PoolPageTypeNameModal
        isOpen={dialogs.showPageTypeNames}
        onClose={() => setDialogs((prev) => ({ ...prev, showPageTypeNames: false }))}
      />

      {/* Pool Management Modal */}
      <PoolManagementModal
        isOpen={dialogs.showPoolManagement}
        onClose={() => setDialogs((prev) => ({ ...prev, showPoolManagement: false }))}
      />

      {/* Mobile filter drawer backdrop */}
      {isFilterOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsFilterOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile filter drawer panel — slides in from the right */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex w-80 flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out dark:bg-dark-800 md:hidden ${
          isFilterOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-dark-600">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Filters</h2>
            {activeFilterCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-xs font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsFilterOpen(false)}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-700 dark:hover:text-gray-300"
            aria-label="Close filters"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Drawer content */}
        <div className="flex-1 overflow-y-auto p-4">
          {state.isMetadataLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={`drawer-filter-skeleton-${i}`}>
                  <Skeleton className="mb-2 h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <CompanyFilters
              filters={state.filters}
              onFiltersChange={handleFiltersChange}
              pools={state.pools}
              groups={state.groups}
              onClearFilters={handleClearFilters}
              inDrawer
            />
          )}
        </div>

        {/* Drawer footer */}
        <div className="flex gap-3 border-t border-gray-200 p-4 dark:border-dark-600">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              handleClearFilters();
              setIsFilterOpen(false);
            }}
          >
            Clear
          </Button>
          <Button variant="primary" className="flex-1" onClick={() => setIsFilterOpen(false)}>
            Filter
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};
